import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ConfirmDialog from './ConfirmDialog';

const initialState = {
  catchOnCancel: true,
  disableBackdropClick: false,
  title: null,
  message: null,
  buttons: undefined,
  dialogProps: {},
};
const ConfirmationServiceContext = createContext(initialState);

export const useConfirm = () => useContext(ConfirmationServiceContext);

export const ConfirmationServiceProvider = ({ children }) => {
  const [confirmationState, setConfirmationState] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef(null);
  const awaitingPromiseRef = useRef(null);

  useEffect(() => {
    if (isOpen && timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, [isOpen]);

  const openConfirmation = options => {
    setConfirmationState({ ...initialState, ...options });
    setIsOpen(true);
    return new Promise((resolve, reject) => {
      // save the promise result to the ref
      awaitingPromiseRef.current = { resolve, reject };
    });
  };

  const handleClose = response => {
    // Mostly always you don't need to handle canceling of alert dialog
    // So shutting up the unhandledPromiseRejection errors
    if (confirmationState.catchOnCancel && awaitingPromiseRef.current) {
      awaitingPromiseRef.current.reject(response);
    }

    if (!confirmationState.catchOnCancel && awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve('cancel');
    }

    setIsOpen(false);

    timerRef.current = setTimeout(() => {
      setConfirmationState(null);
      awaitingPromiseRef.current = null;
    }, 300);
  };

  const handleSubmit = response => {
    if (awaitingPromiseRef.current) {
      awaitingPromiseRef.current.resolve(response);
    }

    setIsOpen(false);

    timerRef.current = setTimeout(() => {
      setConfirmationState(null);
      awaitingPromiseRef.current = null;
    }, 300);
  };

  return (
    <>
      <ConfirmationServiceContext.Provider value={openConfirmation}>
        {children}
      </ConfirmationServiceContext.Provider>
      <ConfirmDialog
        open={isOpen}
        onSubmit={handleSubmit}
        onClose={handleClose}
        {...confirmationState}
      />
    </>
  );
};

ConfirmationServiceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
